import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Printer } from "lucide-react";
import { useEffect } from "react";

interface TextReportProps {
  report?: string;
  assessmentId?: string;
  organizationName?: string;
  reportingYear?: string;
}

export default function TextReport() {
  const [, setLocation] = useLocation();

  const normalizeMarkdown = (raw: string) => {
    let value = raw.trim();

    // Handle payloads returned as a JSON-encoded string
    if (
      (value.startsWith("\"") && value.endsWith("\"")) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      try {
        value = JSON.parse(value);
      } catch {
        // Leave as-is if parsing fails
      }
    }

    // Remove accidental markdown code fences if model included them
    value = value
      .replace(/^```(?:markdown|md)?\s*/i, "")
      .replace(/\s*```$/, "");

    // Decode escaped newlines/tabs when content is mostly escaped text
    if (value.includes("\\n") && !value.includes("\n")) {
      value = value.replace(/\\n/g, "\n");
    }
    if (value.includes("\\t")) {
      value = value.replace(/\\t/g, "\t");
    }

    return value;
  };

  // Get report data from navigation state
  const historyState = window.history.state as any;
  const reportData = (historyState?.report ?? historyState?.state?.report) as TextReportProps | undefined;

  useEffect(() => {
    // If no report data, redirect back
    if (!reportData?.report) {
      // Try to go back in history, or redirect to home
      if (window.history.length > 1) {
        window.history.back();
      } else {
        setLocation("/");
      }
    }
  }, [reportData, setLocation]);

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    if (reportData?.assessmentId) {
      setLocation(`/report/${reportData.assessmentId}`);
    } else {
      setLocation("/report");
    }
  };

  if (!reportData?.report) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Print-hidden controls */}
      <div className="print:hidden sticky top-0 z-10 bg-background border-b shadow-sm">
        <div className="container max-w-5xl mx-auto py-4 flex items-center justify-between gap-4">
          <Button
            variant="outline"
            className="gap-2"
            onClick={handleBack}
          >
            <ArrowLeft className="h-4 w-4" />
            {"Back to Report"}
          </Button>
          
          <div className="flex-1 text-center">
            <h2 className="text-lg font-semibold">
              {`${reportData.organizationName} - Sustainability Report ${reportData.reportingYear}`}
            </h2>
          </div>

          <Button
            className="gap-2 shadow-lg shadow-primary/20"
            style={{ backgroundColor: '#00444C' }}
            onClick={handlePrint}
          >
            <Printer className="h-4 w-4" />
            {"Print / Save PDF"}
          </Button>
        </div>
      </div>

      {/* Report content */}
      <div className="container max-w-5xl mx-auto py-8 print:py-0">
        <Card className="border-2 border-primary/20 print:border-0 print:shadow-none">
          <CardHeader className="print:hidden">
            <CardTitle className="text-2xl">{"VSME Sustainability Report"}</CardTitle>
            <CardDescription>
              {"AI-generated report based on your assessment"}
            </CardDescription>
          </CardHeader>
          <CardContent className="print:p-0">
            {/* Markdown content with professional styling */}
            <div
              data-testid="text-report-markdown"
              className="max-w-none text-base leading-7
              [&_h1]:font-heading [&_h1]:text-4xl [&_h1]:font-bold [&_h1]:mb-6 [&_h1]:mt-8 [&_h1]:text-[#001F24]
              [&_h2]:font-heading [&_h2]:text-3xl [&_h2]:font-semibold [&_h2]:mb-4 [&_h2]:mt-6 [&_h2]:text-[#00444C] [&_h2]:border-b [&_h2]:border-[#7ECCC5]/30 [&_h2]:pb-2
              [&_h3]:font-heading [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:mb-3 [&_h3]:mt-5 [&_h3]:text-[#009687]
              [&_h4]:font-heading [&_h4]:text-xl [&_h4]:font-medium [&_h4]:mb-2 [&_h4]:mt-4
              [&_p]:mb-4 [&_p]:text-base [&_p]:leading-7
              [&_strong]:text-[#001F24] [&_strong]:font-semibold
              [&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6
              [&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6
              [&_li]:mb-2 [&_li]:leading-7
              [&_a]:text-[#009687] [&_a]:no-underline hover:[&_a]:underline
              [&_blockquote]:border-l-4 [&_blockquote]:border-[#00444C] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-muted-foreground
              [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono [&_code]:text-[#009687]
              [&_pre]:bg-muted [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto
              [&_table]:w-full [&_table]:border-collapse [&_table]:my-6
              [&_thead]:bg-[#00444C]/10
              [&_th]:border [&_th]:border-gray-300 [&_th]:px-4 [&_th]:py-3 [&_th]:text-left [&_th]:font-semibold [&_th]:text-[#001F24]
              [&_td]:border [&_td]:border-gray-300 [&_td]:px-4 [&_td]:py-3 [&_td]:text-sm
              [&_tr]:border-b [&_tr]:border-gray-200
              [&_img]:rounded-lg [&_img]:shadow-md
              print:text-black print:[&_h1]:break-after-avoid print:[&_h2]:break-after-avoid print:[&_h3]:break-after-avoid print:[&_h4]:break-after-avoid
            ">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {normalizeMarkdown(reportData.report)}
              </ReactMarkdown>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Print-specific styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 2cm;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }

          /* Hide everything except the report content */
          .print\\:hidden {
            display: none !important;
          }

          /* Remove card styling for print */
          [data-testid="text-report-markdown"] {
            max-width: 100% !important;
            font-size: 11pt;
            line-height: 1.5;
          }

          /* Ensure proper page breaks */
          h1, h2, h3, h4, h5, h6 {
            page-break-after: avoid;
            page-break-inside: avoid;
          }

          table {
            page-break-inside: avoid;
          }

          /* Adjust table sizing for print */
          table {
            font-size: 10pt;
          }

          th, td {
            padding: 8px !important;
          }
        }
      `}</style>
    </div>
  );
}
